import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useDataService } from '../context/DataContext';
import useResponsive from '../hooks/useResponsive';
import LoadingSpinner from '../components/ui/LoadingSpinner';

/**
 * VINTAGE BLOG POST PAGE
 * 
 * Design Philosophy: Academic paper layout
 * - Classic typography for optimal readability
 * - Academic paper width (65 characters)
 * - Generous line spacing
 * - Minimal distractions
 */

const VintageBlogPost = () => {
  const { slug } = useParams();
  const { isMobile } = useResponsive();
  const dataService = useDataService();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadPost = async () => {
      try {
        setLoading(true);
        setError(null);
        const postData = await dataService.getPost(slug);
        setPost(postData);
        
        // Load related posts from same category
        if (postData.category_slug || postData.categorySlug) {
          try {
            const allPosts = await dataService.getPosts({ limit: 20 });
            const postsList = Array.isArray(allPosts) ? allPosts : (allPosts.posts || []);
            const related = postsList
              .filter(p => (p.category_slug === postData.category_slug || p.categorySlug === postData.category_slug || p.categorySlug === postData.categorySlug) 
                && p.id !== postData.id && p.slug !== slug)
              .slice(0, 3);
            setRelatedPosts(related);
          } catch (relErr) {
            console.error('Error loading related posts:', relErr);
          }
        }
      } catch (err) {
        console.error('Error loading post:', err);
        setError(err.message);
        // Fallback to mock data
        try {
          const { blogPosts } = await import('../data/mock');
          const mockPost = blogPosts.find(p => p.slug === slug);
          if (mockPost) {
            setPost(mockPost);
            setError(null);
            // Set related posts from mock
            const related = blogPosts
              .filter(p => p.categorySlug === mockPost.categorySlug && p.id !== mockPost.id)
              .slice(0, 3);
            setRelatedPosts(related);
          }
        } catch (mockErr) {
          console.error('Error loading mock post:', mockErr);
        }
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      loadPost();
    }
  }, [slug, dataService]);

  if (loading) {
    return (
      <div className="min-h-screen bg-vintage-cream flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error && !post) {
    return (
      <div className="min-h-screen bg-vintage-cream flex items-center justify-center">
        <div className="text-center">
          <h1 className="vintage-heading-2 text-2xl text-vintage-ink mb-4">Post not found</h1>
          <Link to="/blog" className="text-vintage-accent hover:text-vintage-ink no-underline">
            ← Back
          </Link>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-vintage-cream flex items-center justify-center">
        <div className="text-center">
          <h1 className="vintage-heading-2 text-2xl text-vintage-ink mb-4">Post not found</h1>
          <Link to="/blog" className="text-vintage-accent hover:text-vintage-ink no-underline">
            ← Back
          </Link>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long', 
      day: 'numeric'
    });
  };

  // Process markdown bold syntax (**text**)
  const processBoldText = (text) => {
    if (!text || typeof text !== 'string') return text;
    
    // Split by ** markers and process
    const parts = text.split(/(\*\*.*?\*\*)/g);
    
    return parts.map((part, idx) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        // This is a bold section
        const boldText = part.slice(2, -2); // Remove ** markers
        return (
          <strong key={idx} className="font-semibold text-vintage-ink">
            {boldText}
          </strong>
        );
      }
      // Regular text
      return <span key={idx}>{part}</span>;
    });
  };

  return (
    <div className="min-h-screen bg-vintage-cream">
      <div className="max-w-4xl mx-auto px-6 py-12">
        
        {/* Navigation */}
        <nav className="mb-8">
          <Link 
            to="/blog" 
            className="text-vintage-ink hover:text-vintage-accent flex items-center space-x-2 transition-colors no-underline"
          >
            <ArrowLeft size={16} />
            <span className="typewriter text-sm">Back</span>
          </Link>
        </nav>

        {/* Article Container - Academic Paper Style */}
        <article className="academic-paper">
          
          {/* Article Header */}
          <header className="text-center mb-12">
            {/* Publication Date */}
            <div className="vintage-caption text-vintage-faded mb-4">
              {formatDate(post.published_at || post.publishedAt)}
            </div>
            
            {/* Title */}
            <h1 className="vintage-heading-1 text-3xl md:text-4xl text-vintage-ink mb-6 leading-tight">
              {post.title}
            </h1>
            
            {/* Subtitle/Excerpt */}
            {(post.excerpt || post.description) && (
              <div className="vintage-quote mb-8">
                {post.excerpt || post.description}
              </div>
            )}

            {/* Article Meta */}
            <div className="flex items-center justify-center space-x-6 text-sm py-4" style={{ borderTop: '1px solid var(--vintage-accent)', borderBottom: '1px solid var(--vintage-accent)' }}>
              {post.category && (
                <span className="vintage-badge">
                  {post.category}
                </span>
              )}
              {(post.read_time || post.readTime) && (
                <span className="vintage-caption">
                  {post.read_time || post.readTime}
                </span>
              )}
              <span className="vintage-caption">
                By {post.author || 'Jaswanth Vasudeva'}
              </span>
            </div>
          </header>

          {/* Article Content */}
          <div className="vintage-body text-vintage-ink">
            {(post.content || '').split('\n').map((paragraph, index) => {
              // Handle different markdown elements
              if (paragraph.startsWith('# ')) {
                return (
                  <h1 key={index} className="vintage-heading-1 text-3xl text-vintage-ink mt-12 mb-6 first:mt-0">
                    {processBoldText(paragraph.replace('# ', ''))}
                  </h1>
                );
              }
              
              if (paragraph.startsWith('## ')) {
                return (
                  <h2 key={index} className="vintage-heading-2 text-2xl text-vintage-ink mt-10 mb-5">
                    {processBoldText(paragraph.replace('## ', ''))}
                  </h2>
                );
              }
              
              if (paragraph.startsWith('### ')) {
                return (
                  <h3 key={index} className="vintage-heading-3 text-vintage-ink mt-8 mb-4">
                    {processBoldText(paragraph.replace('### ', ''))}
                  </h3>
                );
              }
              
              // List items
              if (paragraph.match(/^\d+\.\s/) || paragraph.startsWith('- ')) {
                const content = paragraph.replace(/^\d+\.\s|^-\s/, '');
                return (
                  <div key={index} className="flex items-start mb-3">
                    <span className="text-vintage-accent mr-4 typewriter">•</span>
                    <span className="vintage-body">{processBoldText(content)}</span>
                  </div>
                );
              }
              
              // Italic text (publication info)
              if (paragraph.startsWith('*') && paragraph.endsWith('*') && paragraph.length > 2) {
                return (
                  <div key={index} className="text-center mt-12 pt-8" style={{ borderTop: '1px solid var(--vintage-accent)' }}>
                    <p className="vintage-caption text-vintage-faded italic">
                      {paragraph.replace(/^\*/, '').replace(/\*$/, '')}
                    </p>
                  </div>
                );
              }
              
              // Empty lines
              if (paragraph.trim() === '') {
                return <div key={index} className="h-6" />;
              }
              
              // Regular paragraphs
              return (
                <p key={index} className="vintage-body mb-6 leading-relaxed">
                  {processBoldText(paragraph)}
                </p>
              );
            })}
          </div>

          {/* Article Footer */}
          <footer className="mt-16 pt-8" style={{ borderTop: '2px solid var(--vintage-accent)' }}>
            <div className="text-center">
              <div className="vintage-caption text-vintage-faded mb-4">
                Published on {formatDate(post.published_at || post.publishedAt)}
              </div>
              
              {/* Tags */}
              {post.tags && post.tags.length > 0 && (
                <div className="flex flex-wrap items-center justify-center gap-2 mb-6">
                  {post.tags.map((tag) => (
                    <span key={tag} className="vintage-caption text-vintage-faded">
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
              
              {/* Navigation */}
              <Link 
                to="/blog"
                className="vintage-btn"
              >
                Return to Blogs & Essays
              </Link>
            </div>
          </footer>
          
        </article>


      </div>
    </div>
  );
};

export default VintageBlogPost;